import React from "react";

import classNames from "classnames";
import _ from "lodash";

import { compose, graphql } from "lib/apolloClient";
import { EmptyState, Tooltip } from "@toddle-edu/ds-web";
import {
  ChatIllustration,
  NoSearchInFilterIllustration,
} from "@toddle-edu/ds-theme";
import { onEnterOrSpaceKeyDown, prepareLoadMoreFeed } from "Utils";

import { useI18n } from "Hooks";
import MessagingAvatar from "MessagingV2/components/ThreadList/Avatar";
import {
  CHAT_TYPE_ENUM,
  getIsMessagingWidget,
} from "MessagingV2/modules/Utils";

import { getOrganizationId } from "modules/CommonSelectors";
import Loading from "UIComponents/Loading";
import withLoader from "UIComponents/GqlWithLoader";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { useDispatch } from "Hooks/useDispatch";
import { useHandleScroll } from "Utils/CustomHooks";
import { getDepartmentsQuery } from "MessagingV2/modules/MessagingQuery";
import { useChatOptions } from "MessagingV2/providers/ChatOptionsProvider";
import { handleCreateDepartmentChannel } from "MessagingV2/modules/MessagingModule";

import classes from "./DepartmentList.scss";

const styles = {
  customAvatarStyle: {
    size: "medium",
  },
};

const LABEL_LENGTH = 38;

const DepartmentListItem = ({ department = {}, onCloseModal }) => {
  const { t } = useI18n(["common"]);
  const dispatch = useDispatch();

  const { screenMode } = useChatOptions();
  const isWidget = getIsMessagingWidget({ screenMode });

  const { id, name, profileImage, users } = department;

  const totalMembers = _.get(users, "totalCount", 0);

  const onCreateChannelClick = () => {
    dispatch(handleCreateDepartmentChannel({ id, isWidget }));
    onCloseModal();
  };

  const onKeyPressHandle = e => {
    onEnterOrSpaceKeyDown(e, onCreateChannelClick);
  };

  return (
    <div
      className={classes.departmentCard}
      onClick={onCreateChannelClick}
      role="button"
      tabIndex={0}
      aria-label="department-card"
      onKeyPress={onKeyPressHandle}
    >
      <div className={classes.departmentDetailContainer}>
        <MessagingAvatar
          id={id}
          name={name}
          chatType={CHAT_TYPE_ENUM.CHANNEL_DEPARTMENT}
          url={profileImage}
          customAvatarStyles={styles.customAvatarStyle}
          customAvatarContainerClassName={classes.avatarContainer}
        />

        <div className={classes.departmentTitleAndSubtitleContainer}>
          <ConditionalWrapper
            wrapper={<Tooltip tooltip={name} />}
            condition={_.size(name) > LABEL_LENGTH}
            type={"wrapperAsElement"}
          >
            <div className="text-body">
              {_.truncate(name, { length: LABEL_LENGTH })}
            </div>
          </ConditionalWrapper>
          <div className={classNames("text-body-s", classes.subtitle)}>
            {t("common:count_members", {
              count: totalMembers,
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const getEmptyScreenState = ({ searchText, t }) => {
  if (_.isEmpty(searchText)) {
    return {
      illustration: ChatIllustration,
      title: t("common:no_department_yet"),
      subtitle: t("common:department_empty_subtext"),
    };
  }

  return {
    illustration: NoSearchInFilterIllustration,
    title: `${t("common:no_result_found")}!`,
    subtitle: t("common:departments_empty_subtext_with_search"),
  };
};

const DepartmentList = ({
  departments,
  searchText,
  containerRef,
  networkStatus,
  hasNextPage,
  isLoadingMore,
  loadMoreDepartments,
  onCloseModal,
}) => {
  const { t } = useI18n(["common"]);

  useHandleScroll({
    networkStatus,
    loadMoreFeed: loadMoreDepartments,
    hasNextPage,
    getScrollRef: () => containerRef.current,
  });

  if (_.isEmpty(departments)) {
    const { illustration, title, subtitle } = getEmptyScreenState({
      searchText,
      t,
    });

    return (
      <div className={classes.emptyStateContainer}>
        <div className={classes.emptyState}>
          <EmptyState
            illustration={illustration}
            title={title}
            subtitle={subtitle}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {_.map(departments, department => {
        const departmentId = _.get(department, "id");
        return (
          <DepartmentListItem
            key={departmentId}
            department={department}
            onCloseModal={onCloseModal}
          />
        );
      })}
      {isLoadingMore ? (
        <div className={classes.loaderContainer}>
          <Loading />
        </div>
      ) : null}
    </div>
  );
};

export default compose(
  graphql(getDepartmentsQuery, {
    name: "getDepartments",
    options: ({ searchText }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId: getOrganizationId(),
          input: {
            first: 20,
            searchText,
            channelJoinStatus: "NOT_JOINED",
          },
        },
      };
    },
    props({ getDepartments }) {
      const queryVariables = _.get(getDepartments, "variables", {});

      const node = _.get(getDepartments, "node", {});
      const pageInfo = _.get(node, "connectDepartments.pageInfo", {});
      const hasNextPage = _.get(
        node,
        "connectDepartments.pageInfo.hasNextPage",
        false
      );
      const departmentEdges = _.get(node, "connectDepartments.edges", []);
      const departments = _.map(departmentEdges, "node");

      return {
        isLoading: _.includes([1, 2, 3], getDepartments["networkStatus"]),
        isData: !_.isEmpty(node),
        departments,
        networkStatus: getDepartments.networkStatus,
        hasNextPage,
        isLoadingMore: _.isEqual(getDepartments.networkStatus, 3),
        loadMoreDepartments: prepareLoadMoreFeed({
          variables: {
            ...queryVariables,
            input: {
              ...queryVariables.input,
              after: _.get(pageInfo, "endCursor", undefined),
            },
          },
          queryData: getDepartments,
          key: "connectDepartments",
        }),
      };
    },
  }),
  withLoader
)(DepartmentList);
